/*
 * Project: bellcloud
 * File: apps/frontend/src/connections/baseDeviceAdapter.ts
 * Description: Serial/BLE 어댑터 공통 베이스.
 *   single 모드의 요청-응답(pendingPromise) 패턴, stream 큐, 버퍼/모드 공통 로직을 담는다.
 *   전송 매체(포트 read 루프 / GATT notify)와 연결 관리는 하위 클래스가 구현한다.
 */

import type { DeviceAdapter } from './modes/types';
import { DeviceFrameRingParser } from './deviceFrameRingParser';
import { AsyncQueue } from './asyncQueue';
import { pollStreamQueue } from './pollStreamQueue';
import { I18nError } from '@packages/types/errors';
import { i18nKeys } from '@/i18n/i18nKeyGen';

export abstract class BaseDeviceAdapter implements DeviceAdapter {
  protected mode: 'single' | 'stream' = 'single';
  protected pendingPromise: Promise<Uint8Array> | null = null;
  protected pendingResolver: ((value: Uint8Array) => void) | null = null;
  protected readonly packetQueue = new AsyncQueue<Uint8Array>();

  /** single 모드 read() 응답 타임아웃 (ms) */
  protected abstract readonly readTimeoutMs: number;
  /** 응답 대기 상태가 없을 때 던질 i18n 키 */
  protected abstract readonly noPendingResponseKey: string;
  /** 폴링/로그 접두 (예: '[Serial]') */
  protected abstract readonly logPrefix: string;

  constructor(protected readonly frameParser: DeviceFrameRingParser) {}

  get queueLength(): number {
    return this.packetQueue.size;
  }

  get bufferSize(): number {
    return this.frameParser.available();
  }

  /** 하위 클래스에서 read 가능 상태(특성/포트)를 검증 (기본: no-op) */
  protected assertReadable(): void {}

  /** single 모드 write 직후 응답 대기 Promise를 준비 */
  protected armPendingResponse(): void {
    this.pendingPromise = new Promise<Uint8Array>(resolve => {
      this.pendingResolver = resolve;
    });
  }

  /** single 모드에서 수신한 프레임을 대기 중인 read()에 전달 */
  protected dispatchSinglePacket(packet: Uint8Array): boolean {
    if (this.pendingResolver) {
      this.pendingResolver(packet);
      this.pendingResolver = null;
      this.pendingPromise = null;
      return true;
    }
    return false;
  }

  /** 링버퍼에 남은 프레임을 모두 추출해 stream 큐로 옮긴다 */
  protected drainBufferIntoQueue(onPacket?: (packet: Uint8Array) => void): number {
    let count = 0;
    let packet: Uint8Array | null;
    while ((packet = this.frameParser.extractPacket()) !== null) {
      onPacket?.(packet);
      this.packetQueue.push(packet);
      count++;
    }
    return count;
  }

  async read(): Promise<Uint8Array> {
    if (this.mode !== 'single') {
      throw new Error('read() cannot be called in STREAM mode');
    }
    this.assertReadable();
    if (!this.pendingPromise) {
      throw new I18nError(this.noPendingResponseKey);
    }
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Timeout: No response from device')), this.readTimeoutMs);
    });
    const response = await Promise.race([this.pendingPromise, timeoutPromise]);
    this.pendingPromise = null;
    this.pendingResolver = null;
    return response;
  }

  /** 짧은 응답 타임아웃으로 읽기 (폴링용) */
  async readWithTimeout(timeoutMs: number): Promise<Uint8Array> {
    if (this.mode !== 'single') {
      throw new Error('readWithTimeout() cannot be called in STREAM mode');
    }
    this.assertReadable();
    if (!this.pendingPromise) {
      throw new I18nError(this.noPendingResponseKey);
    }
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new I18nError(i18nKeys.config.msg_serialDeviceNoResponse)), timeoutMs);
    });
    const response = await Promise.race([this.pendingPromise, timeoutPromise]);
    this.pendingPromise = null;
    this.pendingResolver = null;
    return response;
  }

  async readStreamPacket(timeoutMs: number = 30000): Promise<Uint8Array> {
    if (this.mode !== 'stream') {
      throw new Error('Not in streaming mode');
    }
    return pollStreamQueue({
      queue: this.packetQueue,
      bufferAvailable: () => this.frameParser.available(),
      timeoutMs,
      logPrefix: this.logPrefix,
    });
  }

  /** 버스트 중단(0x16): 단일 모드로 전환 후 정지 프레임 전송·응답 수신 */
  async stopBurstMode(packet: Uint8Array): Promise<Uint8Array> {
    this.setSinglePacketMode();
    await this.write(packet);
    return this.read();
  }

  clearBuffer(_releaseReader: boolean = false): void {
    this.frameParser.clear();
    this.pendingPromise = null;
    this.pendingResolver = null;
    this.packetQueue.clear();
  }

  abstract write(data: Uint8Array): Promise<void>;
  abstract setSinglePacketMode(): void;
  abstract setStreamingMode(): void;
}
